import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';

const POSITION_OPTIONS_BY_TYPE: Record<string, string[]> = {
  NBA_PLAYOFF: ['G', 'F', 'C'],
  NHL_PLAYOFF: ['C', 'LW', 'RW', 'D', 'G'],
  NFL_PLAYOFF: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'],
  NFL_NO_REPEAT: ['QB', 'RB', 'WR', 'TE'],
  CFP: ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'],
  OLYMPIC_HOCKEY: ['F', 'D', 'G'],
  FOUR_NATIONS: ['F', 'D', 'G'],
};

interface PoolPlayer {
  id: string;
  name: string;
  position: string;
  teamId: string;
  teamName: string;
  teamShortName: string;
  seed: number;
  excluded: boolean;
}

interface PoolPlayersResponse {
  poolId: string;
  poolName: string;
  poolType: string;
  total: number;
  excluded: number;
  active: number;
  players: PoolPlayer[];
}

export function AdminPlayerManager() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<PoolPlayersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterTeam, setFilterTeam] = useState<string>('');
  const [filterPosition, setFilterPosition] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'excluded'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPositionId, setEditingPositionId] = useState<string | null>(null);

  useEffect(() => {
    if (poolId) loadPlayers();
  }, [poolId]);

  async function loadPlayers() {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const res = await api.getPoolPlayersAdmin(poolId!, token);
      setData(res);
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to load players', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleExclude(excluded: boolean) {
    if (selectedIds.size === 0) return;
    try {
      setSaving(true);
      setResult(null);
      const token = await getAccessTokenSilently();
      const res = await api.togglePlayerExclusion(poolId!, Array.from(selectedIds), excluded, token);
      setResult({ message: res.message, type: 'success' });
      setSelectedIds(new Set());
      await loadPlayers();
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to update', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  async function handleExcludeAllFiltered(excluded: boolean) {
    const ids = filteredPlayers.map(p => p.id);
    if (ids.length === 0) return;
    try {
      setSaving(true);
      setResult(null);
      const token = await getAccessTokenSilently();
      const res = await api.togglePlayerExclusion(poolId!, ids, excluded, token);
      setResult({ message: res.message, type: 'success' });
      setSelectedIds(new Set());
      await loadPlayers();
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to update', type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const handlePositionChange = useCallback(async (playerId: string, newPosition: string) => {
    try {
      const token = await getAccessTokenSilently();
      await api.updatePlayerPosition(playerId, newPosition, token);
      // Update local state immediately
      if (data) {
        setData({
          ...data,
          players: data.players.map(p =>
            p.id === playerId ? { ...p, position: newPosition } : p
          ),
        });
      }
      setEditingPositionId(null);
    } catch (err) {
      setResult({ message: err instanceof Error ? err.message : 'Failed to update position', type: 'error' });
    }
  }, [data, getAccessTokenSilently]);

  const positionOptions = data ? (POSITION_OPTIONS_BY_TYPE[data.poolType] || ['G', 'F', 'C', 'D']) : [];

  // Derive unique teams and positions for filters
  const teams = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, { id: string; name: string; shortName: string; seed: number }>();
    data.players.forEach(p => {
      if (!map.has(p.teamId)) {
        map.set(p.teamId, { id: p.teamId, name: p.teamName, shortName: p.teamShortName, seed: p.seed });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.seed - b.seed || a.name.localeCompare(b.name));
  }, [data]);

  const positions = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.players.map(p => p.position))].filter(Boolean).sort();
  }, [data]);

  // Filter players
  const filteredPlayers = useMemo(() => {
    if (!data) return [];
    return data.players.filter(p => {
      if (filterTeam && p.teamId !== filterTeam) return false;
      if (filterPosition && p.position !== filterPosition) return false;
      if (filterStatus === 'active' && p.excluded) return false;
      if (filterStatus === 'excluded' && !p.excluded) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.teamName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [data, filterTeam, filterPosition, filterStatus, searchQuery]);

  function toggleSelect(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  }

  function toggleSelectAll() {
    if (selectedIds.size === filteredPlayers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPlayers.map(p => p.id)));
    }
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-sm text-blue-600 hover:text-blue-800 mb-1 inline-block">&larr; Back to Admin</Link>
          <h1 className="text-2xl font-bold text-gray-900">Manage Players</h1>
          {data && <p className="text-sm text-gray-600 mt-1">{data.poolName}</p>}
        </div>
        {data && (
          <div className="text-right text-sm">
            <p className="text-gray-600">
              <span className="font-semibold text-green-600">{data.active}</span> active &middot;{' '}
              <span className="font-semibold text-red-600">{data.excluded}</span> excluded &middot;{' '}
              <span className="font-semibold">{data.total}</span> total
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Teams</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>
                {t.shortName || t.name} ({t.seed !== 999 ? `#${t.seed}` : '—'})
              </option>
            ))}
          </select>
          <select
            value={filterPosition}
            onChange={(e) => setFilterPosition(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Positions</option>
            {positions.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="excluded">Excluded Only</option>
          </select>
          <div className="text-sm text-gray-500 flex items-center">
            {filteredPlayers.length} shown
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="bg-white rounded-lg shadow p-3 mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={() => handleExclude(true)}
          disabled={saving || selectedIds.size === 0}
          className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-40 transition-colors"
        >
          Exclude Selected ({selectedIds.size})
        </button>
        <button
          onClick={() => handleExclude(false)}
          disabled={saving || selectedIds.size === 0}
          className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-40 transition-colors"
        >
          Restore Selected ({selectedIds.size})
        </button>
        <div className="border-l border-gray-300 h-6 mx-1" />
        <button
          onClick={() => handleExcludeAllFiltered(true)}
          disabled={saving || filteredPlayers.length === 0}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
        >
          Exclude All Filtered ({filteredPlayers.length})
        </button>
        <button
          onClick={() => handleExcludeAllFiltered(false)}
          disabled={saving || filteredPlayers.length === 0}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
        >
          Restore All Filtered ({filteredPlayers.length})
        </button>
        {saving && <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />}
      </div>

      {result && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {result.message}
        </div>
      )}

      {/* Player table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredPlayers.length && filteredPlayers.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Player</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Position</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Team</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Seed</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredPlayers.map(player => (
              <tr
                key={player.id}
                className={`${player.excluded ? 'bg-red-50 text-gray-400' : 'hover:bg-gray-50'} ${selectedIds.has(player.id) ? 'bg-blue-50' : ''}`}
              >
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(player.id)}
                    onChange={() => toggleSelect(player.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className={`px-4 py-2 font-medium ${player.excluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                  {player.name}
                </td>
                <td className="px-4 py-2">
                  {editingPositionId === player.id ? (
                    <select
                      autoFocus
                      value={player.position}
                      onChange={(e) => handlePositionChange(player.id, e.target.value)}
                      onBlur={() => setEditingPositionId(null)}
                      className="px-2 py-0.5 border border-blue-400 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {!positionOptions.includes(player.position) && (
                        <option value={player.position}>{player.position || '(none)'}</option>
                      )}
                      {positionOptions.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  ) : (
                    <button
                      onClick={() => setEditingPositionId(player.id)}
                      className={`px-2 py-0.5 rounded text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${
                        player.position ? 'text-gray-600' : 'text-red-500 italic'
                      }`}
                      title="Click to edit position"
                    >
                      {player.position || 'Set position'}
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">{player.teamShortName || player.teamName}</td>
                <td className="px-4 py-2 text-gray-600">{player.seed !== 999 ? `#${player.seed}` : '—'}</td>
                <td className="px-4 py-2">
                  {player.excluded ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Excluded
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filteredPlayers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No players match the current filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
